import {
  Animated,
  Easing,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
} from 'react-native';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {CustomText} from '../../text';
import {OnOffToggleSwitchProps} from './type';
import {useColorThemeType} from '@buncha/appStyles/hooks/theme';

const OnOffToggleSwitch: React.FC<OnOffToggleSwitchProps> = props => {
  const {
    width = 70,
    height = 30,
    circleWidth = 26,
    circleHeight = 26,
    value = false,
    onText = 'ON',
    offText = 'OFF',
    disabled = false,
    onToggle,
    onTextColor,
    offTextColor,
    backgroundActive,
    backgroundInactive,
    circleActiveColor,
    circleInActiveColor,
    textSize,
  } = props;

  const [isOn, setIsOn] = useState<boolean>(value || false);
  const animatedValue = useRef(new Animated.Value(value ? 1 : 0)).current;

  const theme = useColorThemeType();

  const getColor = useMemo(() => {
    return {
      active: backgroundActive ?? theme?.background.green[100] ?? '',
      inActive: backgroundInactive ?? theme?.background.gray[100] ?? '',
      circleActive: circleActiveColor ?? theme?.background.white[100],
      circleInActive: circleInActiveColor ?? theme?.background.white[100],
      textActive: onTextColor ?? theme?.background.white[100] ?? '',
      textInActive: offTextColor ?? theme?.background.white[100] ?? '',
    };
  }, [
    backgroundActive,
    backgroundInactive,
    circleActiveColor,
    circleInActiveColor,
    offTextColor,
    onTextColor,
    theme?.background.gray,
    theme?.background.green,
    theme?.background.white,
  ]);

  useEffect(() => {
    Animated.timing(animatedValue, {
      toValue: isOn ? 1 : 0,
      duration: 300,
      easing: Easing.linear,
      useNativeDriver: false,
    }).start();
  }, [isOn, animatedValue]);

  const toggleSwitch = () => {
    setIsOn(!isOn);
    if (onToggle) onToggle(!isOn);
  };

  const translateX = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [2, (width || 0) - (circleWidth || 0) - 2],
  });

  const backgroundColor = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [getColor.inActive, getColor.active],
  });

  const getTextAlignProps: TextStyle = useMemo(() => {
    if (isOn)
      return {
        textAlign: 'left',
        color: getColor.textActive,
        fontSize: textSize,
      };
    return {
      textAlign: 'right',
      color: getColor.textInActive,
      fontSize: textSize,
    };
  }, [getColor.textActive, getColor.textInActive, isOn, textSize]);

  const getCircleColor = useMemo(() => {
    return isOn ? getColor.circleActive : getColor.circleInActive;
  }, [getColor.circleActive, getColor.circleInActive, isOn]);

  useEffect(() => {
    setIsOn(value);
  }, [value]);

  return (
    <TouchableOpacity
      onPress={toggleSwitch}
      activeOpacity={0.8}
      disabled={disabled}>
      <Animated.View
        style={[styles.switchContainer, {backgroundColor, width, height}]}>
        <Animated.View
          style={[
            styles.switchCircle,
            {
              transform: [{translateX}],
              backgroundColor: getCircleColor,
              width: circleWidth,
              height: circleHeight,
            },
          ]}
        />
        <CustomText style={[styles.switchText, getTextAlignProps]}>
          {isOn ? onText : offText}
        </CustomText>
      </Animated.View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  switchContainer: {
    borderRadius: 15,
    justifyContent: 'center',
    position: 'relative',
  },
  switchCircle: {
    borderRadius: 15,
    position: 'absolute',
    top: 2,
  },
  switchText: {
    position: 'absolute',
    left: 0,
    right: 0,
    fontWeight: 'bold',
    paddingHorizontal: 10,
  },
});

export default React.memo(OnOffToggleSwitch);
